import React, { useEffect, useMemo, useRef } from 'react';

// The new Negavid embed can be pasted as a code, URL, iframe, or full wrapper.
function parseEmbed(value, mediaType) {
    const input = String(value || '').trim();
    if (!input) return null;

    let src = input;
    let videoKey = '';
    if (input.includes('<')) {
        const doc = new DOMParser().parseFromString(input, 'text/html');
        const iframe = doc.querySelector('iframe');
        const keyedWrapper = doc.querySelector('[data-video-key]');
        src = iframe ? iframe.getAttribute('src') || '' : '';
        videoKey = keyedWrapper ? keyedWrapper.getAttribute('data-video-key') || '' : '';
    } else if (/^[a-zA-Z0-9_-]+$/.test(input)) {
        src = `https://play.negavid.com/stream/${mediaType}/embed/code/${input}`;
    }

    try {
        const url = new URL(src);
        const code = url.pathname.split('/').pop();
        if (url.protocol !== 'https:' || !['play.negavid.com', 'open.negavid.com'].includes(url.hostname) ||
            !url.pathname.startsWith(`/stream/${mediaType}/embed/code/`) || !/^[a-zA-Z0-9_-]+$/.test(code)) return null;
        return { src: url.href, videoKey, code };
    } catch (_) {
        return null;
    }
}

function createVideoKey() {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();

    const bytes = new Uint8Array(16);
    if (window.crypto?.getRandomValues) {
        window.crypto.getRandomValues(bytes);
        return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
    }

    return `${Date.now().toString(16)}${Math.random().toString(16).slice(2)}`;
}

const NegavidNewPlayerComponent = ({ embed, mediaType = 'video', message = [] }) => {
    const wrapperRef = useRef(null);
    const iframeRef = useRef(null);
    const videoKeyRef = useRef(null);
    if (!videoKeyRef.current) videoKeyRef.current = createVideoKey();
    const parsed = useMemo(() => parseEmbed(embed, mediaType), [embed, mediaType]);

    useEffect(() => {
        const iframe = iframeRef.current;
        const wrapper = wrapperRef.current;
        if (!iframe || !wrapper || !parsed) return;

        // Matches the message and fullscreen behavior of the supplied Negavid hook.
        const sendDrmMessage = () => {
            if (iframe.contentWindow) iframe.contentWindow.postMessage(
                { action: 'NegavidPlayerDRM', data: message }, new URL(parsed.src).origin
            );
        };
        const handleMessage = (event) => {
            const eventData = event.data || {};
            if (event.origin !== new URL(parsed.src).origin || event.source !== iframe.contentWindow ||
                !eventData.videoKey || (parsed.videoKey && eventData.videoKey !== parsed.videoKey)) return;
            if (eventData.event === 'NEGAVID_PLAYER_enterfullscreen') {
                Object.assign(wrapper.style, {
                    position: 'absolute', top: '0', right: '0', width: '100%', height: '100%',
                    padding: '0', paddingBottom: '0', zIndex: '8994816'
                });
            } else if (eventData.event === 'NEGAVID_PLAYER_exitfullscreen') {
                Object.assign(wrapper.style, {
                    position: 'relative', top: '', right: '', width: '100%', height: '0',
                    padding: '0', paddingTop: '25px', paddingBottom: '56.25%', zIndex: '1'
                });
            }
        };

        iframe.addEventListener('load', sendDrmMessage);
        window.addEventListener('message', handleMessage);
        return () => {
            iframe.removeEventListener('load', sendDrmMessage);
            window.removeEventListener('message', handleMessage);
        };
    }, [parsed, message]);

    if (!parsed) return <div>کد پلیر جدید نگاوید معتبر نیست.</div>;

    return (
        <div ref={wrapperRef} className={mediaType === 'audio' ? 'negavid-audio-stream' : 'negavid-video-stream'}
             data-video-key={parsed.videoKey || videoKeyRef.current}
             style={{ position: 'relative', paddingBottom: '56.25%', paddingTop: '25px', height: 0, overflow: 'hidden' }}>
            <iframe ref={iframeRef} id={parsed.code} title={mediaType === 'audio' ? 'Negavid Podcast' : 'Negavid Video'}
                    src={parsed.src} allowFullScreen
                    style={{ border: 'none', position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }} />
        </div>
    );
};

export default NegavidNewPlayerComponent;
